import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AuthUser from "./AuthUser";
import TaskSkeleton from "./TaskSkeleton";
import toast, { Toaster } from "react-hot-toast";
import moment from "moment";
import * as XLSX from "xlsx";

const money = (n) =>
  Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

export default function JcPendingReport() {
  const { http } = AuthUser();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [finishedRows, setFinishedRows] = useState([]);
  const [finishedTotals, setFinishedTotals] = useState(null);
  const [branches, setBranches] = useState([]);
  const [ojcBranchId, setOjcBranchId] = useState(null);
  const [branch, setBranch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    http.get("/branch").then((res) => {
      const list = res.data.users || [];
      setBranches(list);
      const ojc = list.find((b) => b.branch_name === "OTHER JC");
      if (ojc) setOjcBranchId(ojc.id);
    });
  }, []);

  useEffect(() => {
    fetchReport();
  }, [branch, startDate, endDate]);

  const fetchReport = () => {
    setIsLoading(true);
    const qs = new URLSearchParams();
    if (branch) qs.set("branch", branch);
    if (startDate) qs.set("startdate", startDate);
    if (endDate) qs.set("enddate", endDate);

    http
      .get(`/get_jc_pending_report?${qs.toString()}`)
      .then((res) => {
        setIsLoading(false);
        setRows(Array.isArray(res.data.data) ? res.data.data : []);
        setTotals(res.data.totals || null);
        setFinishedRows(
          Array.isArray(res.data.finished_by_assigner)
            ? res.data.finished_by_assigner
            : []
        );
        setFinishedTotals(res.data.finished_totals || null);
        if (res.data.ojc_branch_id) {
          setOjcBranchId(res.data.ojc_branch_id);
        }
      })
      .catch(() => {
        setIsLoading(false);
        toast.error("Failed to load JC pending report");
      });
  };

  const dashLink = (agentId, status = "Pending", branchOverride = null) => {
    const qs = new URLSearchParams();
    if (agentId) qs.set("assigned_to", String(agentId));
    if (status) qs.set("status", status);
    const dept = branchOverride != null ? branchOverride : branch;
    if (dept) qs.set("department", String(dept));
    return `/jobcart/dashboard-job?${qs.toString()}`;
  };

  const hqFinishedLink = (assignerId) => {
    const qs = new URLSearchParams();
    qs.set("assigned_by", String(assignerId));
    qs.set("status", "Finished");
    if (startDate) qs.set("startdate", startDate);
    if (endDate) qs.set("enddate", endDate);
    return `/dashboard?${qs.toString()}`;
  };

  const toggleExpand = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const exportExcel = () => {
    const sheetRows = rows.map((r, i) => ({
      No: i + 1,
      Agent: r.agent_name,
      Pending: r.pending,
      "Other JC Pending": r.other_jc_pending,
      "Hold HQ": r.hold_hq,
      "Pending Amount": r.pending_amount,
    }));
    if (totals) {
      sheetRows.push({
        No: "",
        Agent: "TOTAL",
        Pending: totals.pending,
        "Other JC Pending": totals.other_jc_pending,
        "Hold HQ": totals.hold_hq,
        "Pending Amount": totals.pending_amount,
      });
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(sheetRows),
      "JC Pending"
    );

    const finishedSheet = finishedRows.map((r, i) => ({
      No: i + 1,
      "Assign By": r.agent_name,
      Finished: r.finished_count,
    }));
    if (finishedTotals) {
      finishedSheet.push({
        No: "",
        "Assign By": "TOTAL",
        Finished: finishedTotals.finished_count,
      });
    }
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(finishedSheet),
      "Finished Assign By"
    );

    XLSX.writeFile(wb, `jc_pending_report_${moment().format("YYYYMMDD_HHmm")}.xlsx`);
  };

  return (
    <div className="container-fluid mt-3">
      <Toaster />
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h3 className="mb-0">JC Pending Report</h3>
          <small className="text-muted">
            Open job cards by agent — click counts to open filtered list
          </small>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-outline-success"
          disabled={rows.length === 0 && finishedRows.length === 0}
          onClick={exportExcel}
        >
          Excel
        </button>
      </div>

      <div className="row g-2 mb-3">
        <div className="col-md-3">
          <select
            className="form-select form-select-sm"
            value={branch}
            onChange={(e) => setBranch(e.target.value)}
          >
            <option value="">All Branches</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.branch_name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-2">
          <input
            type="date"
            className="form-control form-control-sm"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="col-md-2">
          <input
            type="date"
            className="form-control form-control-sm"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="col-md-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => {
              setBranch("");
              setStartDate("");
              setEndDate("");
            }}
          >
            Clear
          </button>
        </div>
      </div>

      {totals && (
        <div className="row g-2 mb-3">
          <div className="col-6 col-md-2">
            <div className="border rounded p-2 bg-light h-100">
              <div className="small text-muted">Agents</div>
              <div className="fs-5 fw-bold">{totals.agents}</div>
            </div>
          </div>
          <div className="col-6 col-md-2">
            <div className="border rounded p-2 h-100" style={{ background: "#fee2e2" }}>
              <div className="small text-muted">Pending / Open</div>
              <div className="fs-5 fw-bold text-danger">{totals.pending}</div>
            </div>
          </div>
          <div className="col-6 col-md-2">
            <div className="border rounded p-2 h-100" style={{ background: "#fce7f3" }}>
              <div className="small text-muted">Other JC Pending</div>
              <div className="fs-5 fw-bold" style={{ color: "#be185d" }}>
                {totals.other_jc_pending || 0}
              </div>
            </div>
          </div>
          <div className="col-6 col-md-2">
            <div className="border rounded p-2 h-100" style={{ background: "#ffedd5" }}>
              <div className="small text-muted">Hold HQ</div>
              <div className="fs-5 fw-bold">{totals.hold_hq}</div>
            </div>
          </div>
          <div className="col-6 col-md-2">
            <div className="border rounded p-2 h-100">
              <div className="small text-muted">Pending ₹</div>
              <div className="fs-6 fw-bold">{money(totals.pending_amount)}</div>
            </div>
          </div>
        </div>
      )}

      <div className="table-responsive border rounded">
        {isLoading ? (
          <TaskSkeleton />
        ) : (
          <table className="table table-hover table-sm align-middle mb-0">
            <thead className="table-primary">
              <tr>
                <th style={{ width: 40 }}>No</th>
                <th>Agent</th>
                <th className="text-center">Pending</th>
                <th className="text-center">Other JC Pending</th>
                <th className="text-center">Hold HQ</th>
                <th className="text-end">Pending ₹</th>
                <th>Open</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-4 text-muted">
                    No data found
                  </td>
                </tr>
              ) : (
                rows.map((r, i) => (
                  <Fragment key={r.assigned_to}>
                    <tr>
                      <td>{i + 1}</td>
                      <td className="fw-semibold text-uppercase">{r.agent_name}</td>
                      <td className="text-center">
                        {r.pending > 0 ? (
                          <Link
                            className="badge bg-danger text-decoration-none"
                            to={dashLink(r.assigned_to, "Pending")}
                            title="View pending JCs"
                          >
                            {r.pending}
                          </Link>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="text-center">
                        {r.other_jc_pending > 0 ? (
                          <Link
                            className="badge text-decoration-none"
                            style={{ background: "#be185d" }}
                            to={dashLink(
                              r.assigned_to,
                              "Pending",
                              ojcBranchId || totals?.ojc_branch_id
                            )}
                            title="View Other JC pending"
                          >
                            {r.other_jc_pending}
                          </Link>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="text-center">
                        {r.hold_hq > 0 ? (
                          <Link
                            className="badge bg-warning text-dark text-decoration-none"
                            to={dashLink(r.assigned_to, "hq")}
                          >
                            {r.hold_hq}
                          </Link>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="text-end">
                        {r.pending_amount > 0 ? money(r.pending_amount) : "—"}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0"
                          onClick={() => toggleExpand(r.assigned_to)}
                        >
                          {expanded[r.assigned_to] ? "Hide" : "Breakdown"}
                        </button>
                      </td>
                    </tr>
                    {expanded[r.assigned_to] && (
                      <tr>
                        <td></td>
                        <td colSpan="6">
                          <div className="d-flex flex-wrap gap-2 py-1">
                            {Object.entries(r.status_breakdown || {}).map(
                              ([st, cnt]) => (
                                <span
                                  key={st}
                                  className="badge bg-light text-dark border"
                                >
                                  {st || "(blank)"}: {cnt}
                                </span>
                              )
                            )}
                            <Link
                              className="btn btn-sm btn-outline-primary ms-auto"
                              to={dashLink(r.assigned_to, "Pending")}
                            >
                              Open pending list →
                            </Link>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))
              )}
            </tbody>
            {totals && rows.length > 0 && (
              <tfoot>
                <tr className="table-secondary fw-bold">
                  <td></td>
                  <td>TOTAL</td>
                  <td className="text-center">{totals.pending}</td>
                  <td className="text-center">{totals.other_jc_pending || 0}</td>
                  <td className="text-center">{totals.hold_hq}</td>
                  <td className="text-end">{money(totals.pending_amount)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        )}
      </div>
      <p className="small text-muted mt-2 mb-4">
        Pending / Open = open JC statuses. Other JC Pending = open cards on OTHER JC
        branch only. Agents with no open work are hidden.
      </p>

      {/* Separate Finished (Assign By) report — all assigners, not mixed with pending */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2 mt-4">
        <div>
          <h4 className="mb-0">Finished by Assign By</h4>
          <small className="text-muted">
            HQ tasks with status Finished, grouped by who Assigned By (all people)
          </small>
        </div>
      </div>

      {finishedTotals && (
        <div className="row g-2 mb-3">
          <div className="col-6 col-md-2">
            <div className="border rounded p-2 bg-light h-100">
              <div className="small text-muted">Assigners</div>
              <div className="fs-5 fw-bold">{finishedTotals.agents}</div>
            </div>
          </div>
          <div className="col-6 col-md-2">
            <div className="border rounded p-2 h-100" style={{ background: "#dbeafe" }}>
              <div className="small text-muted">Finished Total</div>
              <div className="fs-5 fw-bold text-primary">
                {finishedTotals.finished_count || 0}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="table-responsive border rounded mb-4">
        {isLoading ? (
          <TaskSkeleton />
        ) : (
          <table className="table table-hover table-sm align-middle mb-0">
            <thead className="table-primary">
              <tr>
                <th style={{ width: 40 }}>No</th>
                <th>Assign By</th>
                <th className="text-center">Finished</th>
              </tr>
            </thead>
            <tbody>
              {finishedRows.length === 0 ? (
                <tr>
                  <td colSpan="3" className="text-center py-4 text-muted">
                    No finished tasks found
                  </td>
                </tr>
              ) : (
                finishedRows.map((r, i) => (
                  <tr key={r.assigned_by}>
                    <td>{i + 1}</td>
                    <td className="fw-semibold text-uppercase">{r.agent_name}</td>
                    <td className="text-center">
                      {r.finished_count > 0 ? (
                        <Link
                          className="badge bg-primary text-decoration-none"
                          to={hqFinishedLink(r.assigned_by)}
                          title="Open HQ dashboard: Finished + this Assign By"
                        >
                          {r.finished_count}
                        </Link>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {finishedTotals && finishedRows.length > 0 && (
              <tfoot>
                <tr className="table-secondary fw-bold">
                  <td></td>
                  <td>TOTAL</td>
                  <td className="text-center">
                    {finishedTotals.finished_count || 0}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        )}
      </div>
      <p className="small text-muted mt-2 mb-0">
        This list is separate from JC Pending. Example: if Varun Assigned By many
        Finished HQ tasks, he appears here even when he has no open JC pending.
      </p>
    </div>
  );
}
